'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Loader as Loader2 } from 'lucide-react';
import { StripeProduct } from '@/stripe-config';
import { useAuth } from '@/context/AuthContext';
import { createCheckoutSession } from '@/lib/stripe';

interface PricingCardProps {
  product: StripeProduct;
  isPopular?: boolean;
}

export function PricingCard({ product, isPopular = false }: PricingCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleCheckout = async () => {
    if (!user) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    setIsLoading(true);

    try {
      const successUrl = `${window.location.origin}/dashboard?purchase=success`;
      const cancelUrl = `${window.location.origin}/pricing`;

      const { url } = await createCheckoutSession({
        price_id: product.priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode: product.mode,
      });

      console.log('[PricingCard] Redirecting to checkout:', url);
      window.location.href = url;
    } catch (error) {
      console.error('[PricingCard] Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    `${product.name.split(' ')[0]} coins monthly`,
    'AI-powered interview practice',
    'Detailed feedback and scoring',
    'Progress tracking',
    'Multiple interview types'
  ];

  return (
    <Card className={`relative ${isPopular ? 'border-primary shadow-lg scale-105' : ''}`}>
      {isPopular && (
        <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2">
          Most Popular
        </Badge>
      )}
      
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">{product.name}</CardTitle>
        <CardDescription>{product.description}</CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold">
            {product.currencySymbol}{product.price}
          </span>
          <span className="text-muted-foreground">/month</span>
        </div>
      </CardHeader>

      <CardContent>
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        <Button 
          className="w-full" 
          onClick={handleCheckout}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            'Subscribe Now'
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}